import React from 'react';
import Footer from 'shared/components/footer/Footer';
import './services.css';
import {
  ButtonLink,
  FunnelPage,
  FunnelSection,
} from 'shared/components/funnel/FunnelUI';

const serviceRoutes = [
  {
    title: 'VIP resources',
    label: 'Signals',
    route: '/services/vip',
    image: '/assets/images/services/vip/pnl1.png',
    summary: 'Signals, proof previews, and tighter market notes inside Telegram.',
    tags: ['Telegram notes', 'Manual payment'],
    effort: 'Manual payment redemption',
  },
  {
    title: 'AI indicator',
    label: 'Charts',
    route: '/services/indicator',
    image: '/assets/images/services/indicator/indicatorSOL.png',
    summary: 'Chart confirmation for traders who manage their own entries.',
    tags: ['Chart tool', '199 USDT'],
    effort: '199 USDT access',
  },
  {
    title: 'Copy trading',
    label: 'Account setup',
    route: '/services/copy-trading',
    image: '/assets/images/services/copy/profitcard.png',
    summary: 'Account setup path for users who want guided copy access.',
    tags: ['UID proof', 'Discord ticket'],
    effort: 'Highest setup effort',
  },
  {
    title: 'Portfolio access',
    label: 'Watchlist',
    route: '/services/portfolio',
    image: '/assets/images/services/tradingData.jpg',
    summary: 'Watchlist access for broader market context and route planning.',
    tags: ['Partner route', 'Proof required'],
    effort: 'Verification required',
  },
];

const decisionSteps = [
  {
    number: '01',
    title: 'Start free',
    copy: 'Join Telegram first and see the daily context before committing to anything.',
  },
  {
    number: '02',
    title: 'Compare by effort',
    copy: 'Each route shows the required action, setup friction, and best-fit use case.',
  },
  {
    number: '03',
    title: 'Move when it fits',
    copy: 'Upgrade only when the route fits your process.',
  },
];

function ServicesConsole() {
  return (
    <div className="services-console" aria-label="Services route preview">
      <div className="services-console-orbit" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <div className="services-console-header">
        <span>CRYPTOXNFT ROUTES</span>
        <strong>FREE FIRST</strong>
      </div>
      <div className="services-console-main">
        <div className="services-console-node services-console-node--primary">
          <span>Entry</span>
          <strong>Free Telegram</strong>
          <small>Market context before paid paths</small>
        </div>
        {serviceRoutes.map((service) => (
          <div className="services-console-node" key={service.title}>
            <span>{service.label}</span>
            <strong>{service.title}</strong>
            <small>{service.effort}</small>
          </div>
        ))}
      </div>
      <div className="services-console-feed">
        <span>Context first</span>
        <span>Proof visible</span>
        <span>Requirements clear</span>
      </div>
    </div>
  );
}

function Services() {
  return (
    <FunnelPage className="services-page">
      <FunnelSection tone="hero" className="services-hero-section">
        <div className="services-hero-grid">
          <div className="services-hero-copy">
            <p className="funnel-eyebrow">Services</p>
            <h1>Services after Telegram.</h1>
            <p>
              Join Telegram first. Then compare VIP resources, the AI indicator,
              copy trading, and portfolio access by fit, effort, and required action.
            </p>
            <div className="services-hero-actions">
              <ButtonLink href="https://t.me/cryptoxnfts">Join Free Telegram</ButtonLink>
              <ButtonLink href="#services-routes" variant="secondary">Compare Services</ButtonLink>
            </div>
            <div className="services-hero-stats" aria-label="Service summary">
              <div>
                <strong>Free</strong>
                <span>first step</span>
              </div>
              <div>
                <strong>4</strong>
                <span>optional paths</span>
              </div>
              <div>
                <strong>Clear</strong>
                <span>requirements</span>
              </div>
            </div>
          </div>
          <ServicesConsole />
        </div>
      </FunnelSection>

      <FunnelSection className="services-path-section" id="services-routes">
        <div className="services-section-heading">
          <p className="funnel-eyebrow">Pick by fit</p>
          <h2>Every service has a different job.</h2>
          <p>
            Use the cards to choose the path that matches your trading style,
            setup tolerance, and preferred level of support.
          </p>
        </div>
        <div className="services-route-grid">
          {serviceRoutes.map((service, index) => (
            <article className="services-route-card" key={service.title}>
              <div className="services-route-image">
                <img src={service.image} alt={`${service.title} preview`} />
                <span>{String(index + 1).padStart(2, '0')}</span>
              </div>
              <div className="services-route-body">
                <p>{service.label}</p>
                <h3>{service.title}</h3>
                <p className="services-route-summary">{service.summary}</p>
                <div className="services-route-tags" aria-label={`${service.title} details`}>
                  {service.tags.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
                <ButtonLink to={service.route} variant={index === 0 ? 'primary' : 'secondary'}>
                  Open {service.title}
                </ButtonLink>
              </div>
            </article>
          ))}
        </div>
      </FunnelSection>

      <FunnelSection tone="muted" className="services-decision-section">
        <div className="services-decision-layout">
          <div className="services-decision-copy">
            <p className="funnel-eyebrow">How to decide</p>
            <h2>Free first.</h2>
            <p>
              The simplest customer path is still Telegram first. Paid access and
              verification-heavy routes are only useful after the user understands
              the CRYPTOXNFT style and the required setup.
            </p>
          </div>
          <div className="services-step-list">
            {decisionSteps.map((step) => (
              <article key={step.number}>
                <span>{step.number}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.copy}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </FunnelSection>

      <FunnelSection className="services-final-section">
        <div className="services-final-panel">
          <div>
            <p className="funnel-eyebrow">Not sure yet?</p>
            <h2>Start with the channel and decide from live context.</h2>
            <p>
              No account setup is needed to begin. Watch the updates, see the
              proof style, and upgrade only if the route fits your process.
            </p>
          </div>
          <div className="services-final-actions">
            <ButtonLink href="https://t.me/cryptoxnfts">Join Free Telegram</ButtonLink>
            <ButtonLink to="/results" variant="secondary">View Results</ButtonLink>
          </div>
        </div>
      </FunnelSection>

      <Footer />
    </FunnelPage>
  );
}

export default Services;
